import { Button, HStack } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getMarketplaceDemands, getOwnProfile, getOwnWorkload } from '../api'
import { EmptyState } from '../components/EmptyState'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { MarketplaceDemandTable } from '../components/MarketplaceDemandTable'
import type { MarketplaceDemand } from '../types'
import type { WorkloadResponse } from '../api'
import { isDemandOpen } from '../demandStatus'
import { Page } from '@/components/layout/Page'

type MarketplaceFilter = 'open' | 'closed' | 'all'

function getRequestedUnits(demand: MarketplaceDemand, serviceType: string) {
  return demand.buckets?.find(bucket => bucket.serviceType === serviceType)?.requestedUnits ?? 0
}

function getRemainingUnits(workload: WorkloadResponse, serviceType: string) {
  const hours =
    workload.buckets.find(bucket => bucket.serviceType === serviceType)?.remainingCapacityHours ??
    0

  if (serviceType === 'CONSULTING') return hours

  return hours / 8
}

function calculateCapacityFit(
  demand: MarketplaceDemand,
  workload: WorkloadResponse
): 'green' | 'yellow' | 'red' {
  let result: 'green' | 'yellow' | 'red' = 'green'

  for (const bucket of demand.buckets ?? []) {
    const requested = getRequestedUnits(demand, bucket.serviceType)
    const remaining = getRemainingUnits(workload, bucket.serviceType)

    if (requested <= 0) continue

    if (remaining <= 0) return 'red'

    const ratio = requested / remaining

    if (ratio > 1.05) return 'red'
    if (ratio > 1) result = 'yellow'
  }

  return result
}

export function MarketplaceListPage() {
  const navigate = useNavigate()

  const [demands, setDemands] = useState<MarketplaceDemand[]>([])
  const [activeFilter, setActiveFilter] = useState<MarketplaceFilter>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function loadMarketplaceDemands() {
    try {
      setIsLoading(true)
      setError(null)

      const result = await getMarketplaceDemands()

      const profile = await getOwnProfile()
      const year = new Date().getFullYear()
      const workload = await getOwnWorkload(profile.userId, year)

      const demandsWithCapacityFit = result.map(demand => ({
        ...demand,
        capacityFit: calculateCapacityFit(demand, workload),
      }))

      setDemands(demandsWithCapacityFit)
    } catch {
      setError('Could not load marketplace demands.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void loadMarketplaceDemands()
  }, [])

  const filteredDemands = useMemo(() => {
    if (activeFilter === 'all') return demands

    if (activeFilter === 'open') {
      return demands.filter(demand => isDemandOpen(demand))
    }

    return demands.filter(demand => !isDemandOpen(demand))
  }, [activeFilter, demands])

  if (isLoading) {
    return <LoadingState message="Loading marketplace..." />
  }

  if (error) {
    return <ErrorState message={error} onRetry={loadMarketplaceDemands} />
  }

  return (
    <Page.Root>
      <Page.Header
        breadcrumbs={[{ label: 'Dashboard', to: '/app/user/dashboard' }, { label: 'Marktplatz' }]}
        backLabel="Zum Dashboard"
        onBack={() => navigate('/app/user/dashboard')}
        title="Marktplatz"
        meta="Aktuelle Bedarfsausschreibungen"
      />

      <Page.Content>
        <Page.Section>
          <HStack gap={2}>
            <Button
              variant={activeFilter === 'open' ? 'subtle' : 'ghost'}
              onClick={() => setActiveFilter('open')}
            >
              Offen
            </Button>

            <Button
              variant={activeFilter === 'closed' ? 'subtle' : 'ghost'}
              onClick={() => setActiveFilter('closed')}
            >
              Geschlossen
            </Button>

            <Button
              variant={activeFilter === 'all' ? 'subtle' : 'ghost'}
              onClick={() => setActiveFilter('all')}
            >
              Alle
            </Button>
          </HStack>
        </Page.Section>

        <Page.Section>
          {filteredDemands.length === 0 ? (
            <EmptyState
              title="Keine Bedarfsausschreibungen"
              description="Für den ausgewählten Filter sind keine Bedarfsausschreibungen verfügbar."
            />
          ) : (
            <MarketplaceDemandTable
              demands={filteredDemands}
              onOpenDemand={demandId => navigate(`/app/user/marketplace/${demandId}`)}
            />
          )}
        </Page.Section>
      </Page.Content>
    </Page.Root>
  )
}
